import React, { ReactNode } from "react";
import { observer } from "mobx-react";
import { upperCase } from "lodash";

import { getAvatarColorByName } from "./AvatarColor";
import { User, IconSize, Team, Account, Add } from "../../icons";

export enum AvatarSize {
  Small = "small",
  Large = "large",
}

export enum AvatarType {
  User = "user",
  Team = "team",
  Account = "account",
}

export interface IAvatarProps {
  firstName?: string;
  lastName?: string;
  size?: AvatarSize;
  type?: AvatarType;
  isInvite?: boolean;
}

@observer
export default class Avatar extends React.Component<IAvatarProps> {
  public static defaultProps = {
    size: AvatarSize.Small,
    type: AvatarType.User,
  };

  constructor(props: IAvatarProps) {
    super(props);
    const { lastName, firstName } = props;

    if (lastName && !firstName) {
      throw new Error("Avatar was given a last name but no first name");
    }
  }

  public render(): ReactNode {
    const { size, type, isInvite } = this.props;
    const AvatarColor = this.getAvatarColor();

    return (
      <div style={{ backgroundColor: AvatarColor }} className={`avatar avatar--${size} avatar--${type}`}>
        {this.renderInnerAvatar()}
        {isInvite && (
          <div className="avatar__invite">
            <Add />
          </div>
        )}
      </div>
    );
  }

  private getAvatarColor = (): string | undefined => {
    const { lastName, firstName } = this.props;
    return getAvatarColorByName((firstName ?? "") + (lastName ?? ""));
  };

  private renderInnerAvatar = (): ReactNode => {
    if (!!this.props.firstName) {
      return this.renderWithInitials();
    } else {
      return this.renderAvatarIcon();
    }
  };

  private renderWithInitials = (): JSX.Element => {
    const { firstName, lastName } = this.props;
    return <div className="avatar__initials">{this.getInitials(firstName as string, lastName)}</div>;
  };

  private renderAvatarIcon = (): ReactNode => {
    const { type, size } = this.props;
    const avatarIconSize = size === AvatarSize.Large ? IconSize.Medium : IconSize.Small;

    if (type === AvatarType.User) {
      return <User size={avatarIconSize} />;
    }

    if (type === AvatarType.Team) {
      return <Team size={avatarIconSize} />;
    }

    if (type === AvatarType.Account) {
      // Use a different size because this icon has no background color
      return <Account size={IconSize.Medium} />;
    }
  };

  private getInitials(first: string, last?: string): string {
    if (last != null && last.length > 0) {
      return upperCase(`${first[0]}${last[0]}`);
    } else {
      return upperCase(first[0]);
    }
  }
}
